import {useMemo} from 'react';
import {useWindowInnerWidth} from '../../../../../../common/shared/hooks/useWindowInnerWidth';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {CountTriggerTextViewerCoreOption} from '../type/CountTriggerTextViewerCoreOption.interface';
import {FrameSize} from '../type/FrameSize.interface';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {getTextViewerFontFamily} from '../util/getTextViewerFontFamily';
import {getTextViewerFontSizePercent} from '../util/getTextViewerFontSizePercent';
import {getTextViewerLineHeight} from '../util/getTextViewerLineHeight';
import {getTextViewerMargin} from '../util/getTextViewerMargin';

export function useCountTriggerTextViewerCoreOption({
  size,
  isMobile,
}: {
  size: FrameSize;
  isMobile: boolean;
}): CountTriggerTextViewerCoreOption {
  const fontSizeNumber = useTypedSelector(
    state => state.textViewer.settings.fontSize
  );
  const fontSizePercent = useMemo(
    () => getTextViewerFontSizePercent(fontSizeNumber),
    [fontSizeNumber]
  );

  const fontType = useTypedSelector(
    state => state.textViewer.settings.fontFamily
  );
  const fontFamily = useMemo(
    () => getTextViewerFontFamily(fontType),
    [fontType]
  );

  const lineGap = useTypedSelector(state => state.textViewer.settings.lineGap);
  const lineHeight = useMemo(() => getTextViewerLineHeight(lineGap), [lineGap]);

  const marginLevel = useTypedSelector(
    state => state.textViewer.settings.contentMargin
  );
  const margin = useMemo(
    () => getTextViewerMargin({marginLevel, width: size.width, isMobile}),
    [isMobile, marginLevel, size.width]
  );

  const isTwoPageLayout = useTypedSelector(state =>
    isMobile || state.textViewer.settings.readType === TextViewerReadType.Scroll
      ? false
      : state.textViewer.settings.isTwoPageLayout
  );

  const windowInnerWidth = useWindowInnerWidth();
  const twoPagesGap = isTwoPageLayout ? Math.round(windowInnerWidth * 0.06) : 0;

  return {
    fontSizePercent,
    fontFamily,
    lineHeight,
    margin,
    size,
    isTwoColumnLayout: isTwoPageLayout,
    twoPagesGap,
  };
}
